/**
 * Safe navigation helpers for API-provided URLs.
 *
 * API responses can return redirect URLs (e.g. Thinkific course redirects,
 * SAML SLO logout URLs) that are passed directly to window.open() or
 * window.location without an allowlist check.  If an API response is
 * compromised or misconfigured, these sinks can redirect users to phishing
 * pages or execute javascript: URIs. (EC-37)
 *
 * These helpers validate URLs before navigation:
 *  - Only https:// and http:// schemes are allowed for external navigation.
 *  - javascript:, data:, vbscript:, and protocol-relative (//...) URLs are
 *    silently blocked.
 *  - window.open() always uses 'noopener,noreferrer' for _blank targets.
 */

/** Schemes that are safe to navigate to externally. */
const ALLOWED_SCHEMES = ['https:', 'http:'];

/**
 * Returns true when `url` is safe to navigate to.
 *
 * Performs a URL parse rather than regex matching so that
 * canonicalization and percent-encoding edge cases are handled
 * by the browser's own URL parser.
 *
 * @param {string} url
 * @returns {boolean}
 */
export function isSafeExternalUrl(url) {
  if (typeof url !== 'string' || !url) return false;
  try {
    const parsed = new URL(url);
    return ALLOWED_SCHEMES.includes(parsed.protocol);
  } catch {
    // Relative URLs (no scheme) are safe for same-origin navigation
    // but not for external window.open() calls.
    return false;
  }
}

/**
 * Opens `url` in a new tab only if it passes the allowlist check.
 *
 * Always adds 'noopener,noreferrer' to prevent the opened tab from
 * accessing window.opener and to avoid Referer leakage.
 *
 * @param {string} url - URL from an API response or external source.
 * @param {string} [target='_blank'] - Window target.
 * @returns {Window | null} The opened window, or null if blocked.
 */
export function safeWindowOpen(url, target = '_blank') {
  if (!isSafeExternalUrl(url)) {
    console.warn('[safeNavigation] Blocked unsafe window.open URL:', url);
    return null;
  }
  return window.open(url, target, 'noopener,noreferrer');
}

/**
 * Navigates the current window to `url` only if it passes the allowlist check.
 *
 * Returns `true` if navigation was initiated, `false` if the URL was blocked.
 * Callers can use the return value to provide a fallback destination when the
 * API-provided URL is invalid. (BugBot PR-229)
 *
 * @param {string} url - URL from an API response or external source.
 * @param {'assign'|'replace'} [method='assign'] - Navigation method.
 * @returns {boolean} True when navigation was triggered, false when blocked.
 */
export function safeWindowLocation(url, method = 'assign') {
  if (!isSafeExternalUrl(url)) {
    console.warn('[safeNavigation] Blocked unsafe window.location URL:', url);
    return false;
  }
  if (method === 'replace') {
    window.location.replace(url);
  } else {
    window.location.assign(url);
  }
  return true;
}
